import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useETSFDaily, type DailyChecklist, type ChecklistResponse } from '../../../hooks/useETSFDaily'

export default function DailyChecklistDetail() {
  const { id } = useParams<{ id: string }>()
  const { fetchChecklist, fetchResponses } = useETSFDaily()
  const [checklist, setChecklist] = useState<DailyChecklist | null>(null)
  const [responses, setResponses] = useState<ChecklistResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) throw new Error('No checklist ID')
        const c = await fetchChecklist(id)
        setChecklist(c)
        const r = await fetchResponses(id)
        setResponses(r)
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, fetchChecklist, fetchResponses])

  if (loading) return <div className="p-6">Loading...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>
  if (!checklist) return <div className="p-6">Checklist not found</div>

  const groupedBySection = responses.reduce((acc, r) => {
    if (!acc[r.section]) acc[r.section] = []
    acc[r.section].push(r)
    return acc
  }, {} as Record<string, ChecklistResponse[]>)

  const resultColor =
    checklist.overall_result === 'fail'
      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/customs/etsf" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
          ← Back
        </Link>

        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold">Mid-week Checklist</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {new Date(checklist.checklist_date).toLocaleDateString('en-GB')} • {checklist.location}
            </p>
          </div>
          <span className={`px-3 py-1 rounded text-sm font-medium capitalize ${resultColor}`}>
            {checklist.overall_result || '-'}
          </span>
        </div>

        {/* Checklist Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Completed By</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">{checklist.completed_by}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Job Ref</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">{checklist.job_ref || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Location</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">{checklist.location}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {new Date(checklist.checklist_date).toLocaleDateString('en-GB')}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Created At</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {new Date(checklist.created_at).toLocaleDateString('en-GB')}
              </div>
            </div>
          </div>
          {checklist.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Notes</div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{checklist.notes}</p>
            </div>
          )}
        </div>

        {/* Checklist Items by Section */}
        <div className="space-y-6">
          {Object.entries(groupedBySection).map(([section, items]) => (
            <div key={section}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded">
                {section}
              </h2>
              <div className="space-y-2">
                {items.map(item => {
                  const responseColor =
                    item.response === 'no'
                      ? 'text-red-700 dark:text-red-200 bg-red-50 dark:bg-red-900/20'
                      : item.response === 'yes'
                        ? 'text-green-700 dark:text-green-200 bg-green-50 dark:bg-green-900/20'
                        : 'text-gray-600 dark:text-gray-400'
                  return (
                    <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-800/50">
                      <div className="flex justify-between items-start gap-4">
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex-1">{item.item_description}</p>
                        <div className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${responseColor}`}>
                          {item.response.toUpperCase()}
                        </div>
                      </div>
                      {item.notes && (
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic">
                          Note: {item.notes}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
